import * as React from "react";

import { cn } from "@/lib/utils";

const labelClass =
  "mb-[0.35rem] block text-[0.65rem] font-bold uppercase tracking-[0.1em] text-charcoal-600";

const controlClass =
  "box-border w-full rounded-[2px] border border-solid border-[#D1D5DB] bg-white py-[0.75rem] px-[1rem] text-[0.875rem] leading-normal text-charcoal-900 shadow-none outline-none transition-[border-color,box-shadow] duration-200 ease-in-out placeholder:text-gray-400 focus:border-brand-red focus:shadow-[0_0_0_1px_rgba(255,0,0,0.1)] focus:outline-none focus:ring-0 focus:ring-offset-0";

const controlErrorClass =
  "border-brand-red focus:border-brand-red focus:shadow-[0_0_0_1px_rgba(255,0,0,0.2)]";

const inputVariantClass = {
  default: "",
  monoLight:
    "rounded-sm border-gray-300 px-4 py-3 font-mono text-sm focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red",
  monoDark:
    "rounded-sm border-charcoal-700 bg-charcoal-900 px-4 py-4 font-mono text-sm text-white focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red",
} as const;

export type TextInputVariant = keyof typeof inputVariantClass;

type FormFieldProps = {
  id: string;
  label: React.ReactNode;
  error?: string;
  children: (controlProps: {
    id: string;
    "aria-invalid"?: true;
    "aria-describedby"?: string;
  }) => React.ReactNode;
};

export function FormField({ id, label, error, children }: FormFieldProps) {
  const errorId = `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      {children({
        id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": error ? errorId : undefined,
      })}
      <FieldError id={errorId} message={error} />
    </div>
  );
}

type FieldErrorProps = {
  id?: string;
  message?: string;
  className?: string;
};

export function FieldError({ id, message, className }: FieldErrorProps) {
  if (!message) return null;
  return (
    <p
      id={id}
      className={cn("mt-1 text-left text-xs text-brand-red", className)}
      role="alert"
    >
      {message}
    </p>
  );
}

type ControlExtras = { hasError?: boolean };

export function TextInput({
  className,
  hasError,
  variant = "default",
  ...props
}: React.ComponentProps<"input"> & ControlExtras & { variant?: TextInputVariant }) {
  return (
    <input
      className={cn(
        controlClass,
        inputVariantClass[variant],
        hasError && controlErrorClass,
        className,
      )}
      {...props}
    />
  );
}

export function TextArea({
  className,
  hasError,
  ...props
}: React.ComponentProps<"textarea"> & ControlExtras) {
  return (
    <textarea
      className={cn(
        controlClass,
        "resize-none",
        hasError && controlErrorClass,
        className,
      )}
      {...props}
    />
  );
}

const selectChevron = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%235C6B80'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`;

export function SelectInput({
  className,
  hasError,
  style,
  ...props
}: React.ComponentProps<"select"> & ControlExtras) {
  return (
    <select
      className={cn(
        controlClass,
        "appearance-none bg-[length:1rem] bg-[right_0.75rem_center] bg-no-repeat pr-[2.5rem]",
        hasError && controlErrorClass,
        className,
      )}
      style={{ backgroundImage: selectChevron, ...style }}
      {...props}
    />
  );
}
